import { appendEvent } from './dom/eventRenderer';
import { appendMessage, unmarkMessagePending, clearComposer } from './dom/messageRenderer';
import {
  appendAttachmentPreview, appendAttachment, clearAttachmentPreviews, revokeBlobUrls
} from './dom/attachmentRenderer';
import { connectWebSocket, disconnectWebSocket } from './websocketClient';
import { sendMessage, sendAttachment, fetchTranscript } from './apiRoutes';
import { processWebsocketContent } from './websocketEvents';

const MAX_ATTACHMENTS = 5;

let _widgetInitialized = false;
let _awsConnectContactId = null;
let _transcriptRendered = false;
let _pendingWebsocketMessages = [];
let _pendingFiles = [];

function _renderAttachmentPreviews(container) {
  clearAttachmentPreviews(container);
  _pendingFiles.forEach((file, index) => {
    appendAttachmentPreview(container, file, () => {
      _pendingFiles.splice(index, 1);
      _renderAttachmentPreviews(container);
    });
  });
}

function _addFiles(container, fileList) {
  const remaining = MAX_ATTACHMENTS - _pendingFiles.length;
  const newFiles = Array.from(fileList).slice(0, remaining);
  _pendingFiles.push(...newFiles);
  _renderAttachmentPreviews(container);
}

function _sendMessageText(container, text) {
  const pendingMessageId = crypto.randomUUID();
  const messageGroup = appendMessage(container, 'customer', text, 'You', pendingMessageId);

  const promise = sendMessage(_awsConnectContactId, text).then(({ ok, messageId, sessionExpired }) => {
    if (ok) {
      unmarkMessagePending(messageGroup, messageId);
    } else {
      appendEvent(container, 'Your message could not be delivered. Please try again.');
      if (sessionExpired) document.dispatchEvent(new CustomEvent('chatkit.aws.session-expired'));
    }
  });
  return promise;
}

function _sendMessageAttachment(container, file) {
  const pendingMessageId = crypto.randomUUID();
  const messageGroup = appendMessage(container, 'customer', null, 'You', pendingMessageId);
  appendAttachment(messageGroup, file);

  const promise = sendAttachment(_awsConnectContactId, file).then(({ ok, attachmentId, sessionExpired }) => {
    if (ok) {
      unmarkMessagePending(messageGroup, attachmentId);
    } else {
      appendEvent(container, 'Your attachment could not be delivered. Please try again.');
      if (sessionExpired) document.dispatchEvent(new CustomEvent('chatkit.aws.session-expired'));
    }
  });
  return promise;
}

async function _sendMessage(container) {
  const input = container.querySelector('.aws-chatkit-composer-input');
  const text = input.value.trim();
  const attachments = [..._pendingFiles];

  if (!text && attachments.length === 0) return;

  _pendingFiles = [];
  clearComposer(container);
  clearAttachmentPreviews(container);

  const promises = [];
  if (text) promises.push(_sendMessageText(container, text));
  attachments.forEach((file) => promises.push(_sendMessageAttachment(container, file)));
  await Promise.all(promises);
}

function _bindComposer(container) {
  const input = container.querySelector('.aws-chatkit-composer-input');
  const sendBtn = container.querySelector('.aws-chatkit-send-btn');
  if (!input || !sendBtn) return;

  input.addEventListener('input', () => {
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 96)}px`;
  });

  sendBtn.addEventListener('click', () => _sendMessage(container));

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      _sendMessage(container);
    }
  });
}

function _bindLightbox(container) {
  const lightbox = container.querySelector('.aws-chatkit-lightbox');
  const lightboxImg = lightbox.querySelector('.aws-chatkit-lightbox-image');
  const downloadBtn = lightbox.querySelector('.aws-chatkit-lightbox-download');
  const closeBtn = lightbox.querySelector('.aws-chatkit-lightbox-close');

  let _lightboxSrc = '';
  const open = (src) => { _lightboxSrc = src; lightboxImg.src = src; lightbox.classList.add('active') };
  const close = () => { lightbox.classList.remove('active'); lightboxImg.src = ''; _lightboxSrc = '' };

  downloadBtn.addEventListener('click', () => { if (_lightboxSrc) window.open(_lightboxSrc, '_blank'); });

  const openOnClick = (e) => {
    if (e.target.closest('.aws-chatkit-attachment-remove')) return;
    const imageCard = e.target.closest('.aws-chatkit-image-card[data-lightbox-src]');
    if (imageCard) open(imageCard.dataset.lightboxSrc);
  };

  container.querySelector('.aws-chatkit-messages').addEventListener('click', openOnClick);
  const previewArea = container.querySelector('.aws-chatkit-attachments-preview');
  if (previewArea) previewArea.addEventListener('click', openOnClick);

  closeBtn.addEventListener('click', close);
  lightbox.addEventListener('click', (e) => { if (e.target === lightbox) close(); });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('active')) close();
  });
}

function _bindAttachments(container) {
  const attachBtn = container.querySelector('.aws-chatkit-attach-btn');
  const fileInput = container.querySelector('.aws-chatkit-file-input');
  if (!attachBtn || !fileInput) return;

  attachBtn.addEventListener('click', () => {
    if (_pendingFiles.length >= MAX_ATTACHMENTS) return;
    fileInput.click();
  });

  fileInput.addEventListener('change', () => {
    if (fileInput.files.length > 0) {
      _addFiles(container, fileInput.files);
    }
    fileInput.value = '';
  });
}

function showComposer() {
  const composer = document.querySelector('#chatkit-widget-container aws-chatkit .aws-chatkit-composer');
  if (composer) composer.classList.remove('d-none');
}

function hideComposer() {
  const composer = document.querySelector('#chatkit-widget-container aws-chatkit .aws-chatkit-composer');
  if (composer) composer.classList.add('d-none');
}

async function _renderTranscript(chatkit, transcript) {
  for (const item of transcript) {
    // eslint-disable-next-line no-await-in-loop
    await processWebsocketContent(chatkit, item, _awsConnectContactId, { immediate: true });
  }
  for (const message of _pendingWebsocketMessages) {
    // eslint-disable-next-line no-await-in-loop
    await processWebsocketContent(chatkit, message, _awsConnectContactId, { immediate: true });
  }
  _transcriptRendered = true;
  _pendingWebsocketMessages = [];
}

async function _initialize() {
  const chatkit = document.querySelector('#chatkit-widget-container aws-chatkit');
  _bindComposer(chatkit);
  _bindAttachments(chatkit);
  _bindLightbox(chatkit);

  _widgetInitialized = true;
}

export async function activate(awsConnectContactId) {
  _awsConnectContactId = awsConnectContactId;
  _pendingFiles = [];

  const chatkit = document.querySelector('#chatkit-widget-container aws-chatkit');
  if (!chatkit) return;
  chatkit.classList.remove('d-none');

  if (!_widgetInitialized) await _initialize();

  appendEvent(chatkit, 'Connecting to a live agent...');
  connectWebSocket(_awsConnectContactId);
  const transcript = await fetchTranscript(_awsConnectContactId);
  await _renderTranscript(chatkit, transcript);
  showComposer();
}

export function deactivate() {
  const chatkit = document.querySelector('#chatkit-widget-container aws-chatkit');
  if (!chatkit) return;
  chatkit.classList.add('d-none');
  chatkit.querySelector('.aws-chatkit-messages').innerHTML = '';
  hideComposer();

  disconnectWebSocket();

  _awsConnectContactId = null;
  _transcriptRendered = false;
  _pendingWebsocketMessages = [];
  _pendingFiles = [];
}

export function destroy() {
  deactivate();

  const chatkit = document.querySelector('#chatkit-widget-container aws-chatkit');
  if (chatkit) revokeBlobUrls(chatkit);

  _widgetInitialized = false;
}

document.addEventListener('chatkit.aws.websocket-message', (event) => {
  if (!_transcriptRendered) {
    _pendingWebsocketMessages.push(event.detail);
    return;
  }
  const chatkit = document.querySelector('#chatkit-widget-container aws-chatkit');
  if (chatkit) processWebsocketContent(chatkit, event.detail, _awsConnectContactId);
});

document.addEventListener('chatkit.aws.chat-ended', () => {
  hideComposer();
});

document.addEventListener('chatkit.end-chat', () => {
  destroy();
});
